import React, { useEffect, useState } from "react";
import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";
import { useGoogleMaps } from "../../contexts/GoogleMapsContext";
import {
  getDrivingDistance,
  formatDistance,
  formatDuration,
  DrivingDistanceResult,
  LatLng,
} from "../../utils/distanceHelper";

interface LocationPoint extends LatLng {
  name?: string;
}

interface LocationDistanceMapProps {
  origin: LocationPoint;
  destination: LocationPoint;
  height?: string;
  onDistanceComputed?: (result: DrivingDistanceResult | null) => void;
}

const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: "cooperative",
};

const LocationDistanceMap: React.FC<LocationDistanceMapProps> = ({
  origin,
  destination,
  height = "220px",
  onDistanceComputed,
}) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const [distance, setDistance] = useState<DrivingDistanceResult | null>(null);

  useEffect(() => {
    let cancelled = false;

    getDrivingDistance(
      { lat: origin.lat, lng: origin.lng },
      { lat: destination.lat, lng: destination.lng }
    ).then((result) => {
      if (!cancelled) {
        setDistance(result);
        onDistanceComputed?.(result);
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin.lat, origin.lng, destination.lat, destination.lng]);

  const handleMapLoad = (map: google.maps.Map) => {
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(origin);
    bounds.extend(destination);
    map.fitBounds(bounds, 48);
  };

  if (loadError) return null;

  if (!isLoaded) {
    return (
      <div
        className="rounded-lg bg-gray-100 flex items-center justify-center text-sm text-gray-400"
        style={{ height }}
      >
        Loading map...
      </div>
    );
  }

  return (
    <div className="relative rounded-lg overflow-hidden">
      <GoogleMap
        mapContainerStyle={{ width: "100%", height }}
        options={mapOptions}
        onLoad={handleMapLoad}
      >
        <Marker position={origin} label="A" />
        <Marker position={destination} label="B" />
        <Polyline
          path={[origin, destination]}
          options={{
            strokeColor: "#2563eb",
            strokeOpacity: 0.8,
            strokeWeight: 3,
          }}
        />
      </GoogleMap>
      {distance && (
        <div className="absolute top-2 left-2 bg-white/95 rounded-md shadow-md px-3 py-1.5 text-xs font-medium text-gray-700 flex items-center gap-2">
          <span>{formatDistance(distance.distanceMeters)}</span>
          <span className="text-gray-300">•</span>
          <span>{formatDuration(distance.durationSeconds)}</span>
        </div>
      )}
    </div>
  );
};

export default LocationDistanceMap;
